import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Shield, Mail, Phone, MapPin, Lock, Eye, Database, UserCheck, Bell, FileText } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const lastUpdated = "February 2, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last Updated: {lastUpdated}</p>
        </div>

        {/* Introduction */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              At <strong>Swift Move and Clean LLC</strong>, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, protect, and share information when you visit our website or use our moving and cleaning services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using our website or services, you agree to the terms of this Privacy Policy. If you do not agree with these terms, please do not use our website or services.
            </p>
          </CardContent>
        </Card>

        {/* Section 1: Information We Collect */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Database className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">1. Information We Collect</h2>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Personal Information You Provide</h3>
            <p className="text-gray-700 mb-4">When you interact with our website, you may provide us with:</p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li><strong>Contact Information:</strong> Name, email address, phone number, and mailing address</li>
              <li><strong>Service Request Details:</strong> Moving origin and destination addresses, preferred dates, type of service needed, special instructions</li>
              <li><strong>Communication Data:</strong> Messages sent through our contact forms, chatbot conversations, and email correspondence</li>
              <li><strong>Payment Information:</strong> Billing address and payment details (processed securely through third-party payment processors)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Information Collected Automatically</h3>
            <p className="text-gray-700 mb-4">When you visit our website, we automatically collect:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, links clicked</li>
              <li><strong>Location Data:</strong> General geographic location based on IP address</li>
              <li><strong>Cookies and Tracking Technologies:</strong> See Section 5 for details</li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 2: How We Use Your Information */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-green-100 p-3 rounded-lg">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
            </div>
            
            <p className="text-gray-700 mb-4">We use your personal information to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Provide Services:</strong> Process booking requests, schedule moving and cleaning services, and communicate about your appointments</li>
              <li><strong>Generate Quotes:</strong> Provide accurate estimates based on your service requirements</li>
              <li><strong>Customer Support:</strong> Respond to inquiries, resolve issues, and provide assistance through our chatbot and contact forms</li>
              <li><strong>Improve Our Services:</strong> Analyze usage patterns to enhance our website and service offerings</li>
              <li><strong>Marketing Communications:</strong> Send promotional offers and updates (only with your consent)</li>
              <li><strong>Legal Compliance:</strong> Meet legal obligations and protect our rights</li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 3: How We Protect Your Information */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Lock className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">3. How We Protect Your Information</h2>
            </div>
            
            <p className="text-gray-700 mb-4">We implement industry-standard security measures to protect your personal information:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Encryption:</strong> All data transmitted through our website is encrypted using SSL/TLS technology</li>
              <li><strong>Secure Storage:</strong> Personal information is stored on secure servers with restricted access</li>
              <li><strong>Access Controls:</strong> Only authorized personnel have access to your personal information</li>
              <li><strong>Regular Security Audits:</strong> We regularly review and update our security practices</li>
              <li><strong>Payment Security:</strong> Payment information is processed through PCI-compliant third-party processors</li>
            </ul>
            <p className="text-gray-700 mt-4">
              While we take reasonable measures to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
            </p>
          </CardContent>
        </Card>

        {/* Section 4: How We Share Your Information */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-orange-100 p-3 rounded-lg">
                <UserCheck className="h-6 w-6 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">4. How We Share Your Information</h2>
            </div>
            
            <p className="text-gray-700 mb-4">We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Service Partners:</strong> Trusted contractors and team members who assist in providing our moving and cleaning services</li>
              <li><strong>Payment Processors:</strong> Third-party payment services to process transactions securely</li>
              <li><strong>Analytics Providers:</strong> Services that help us understand website usage (in anonymized form)</li>
              <li><strong>Legal Authorities:</strong> When required by law, court order, or to protect our legal rights</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 5: Cookies and Tracking Technologies */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Bell className="h-6 w-6 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">5. Cookies and Tracking Technologies</h2>
            </div>
            
            <p className="text-gray-700 mb-4">Our website uses cookies and similar technologies to enhance your experience:</p>
            
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Types of Cookies We Use</h3>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2 ml-4">
              <li><strong>Essential Cookies:</strong> Required for basic website functionality (e.g., session management)</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
              <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (only with consent)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mb-3">Managing Cookies</h3>
            <p className="text-gray-700">
              You can manage your cookie preferences through our cookie consent banner or your browser settings. Please note that disabling certain cookies may affect website functionality.
            </p>
          </CardContent>
        </Card>

        {/* Section 6: Your Rights */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-teal-100 p-3 rounded-lg">
                <FileText className="h-6 w-6 text-teal-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">6. Your Rights</h2>
            </div>
            
            <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where applicable</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </CardContent>
        </Card>

        {/* Section 7: Data Retention */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Typically:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Service records are retained for 7 years for legal and accounting purposes</li>
              <li>Marketing preferences are retained until you opt out</li>
              <li>Website analytics data is retained for 26 months</li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 8: Children's Privacy */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700">
              Our website and services are not intended for children under 18 years of age. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately.
            </p>
          </CardContent>
        </Card>

        {/* Section 9: Changes to This Policy */}
        <Card className="mb-8 shadow-lg border-0">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically. Continued use of our website after changes constitutes acceptance of the updated policy.
            </p>
          </CardContent>
        </Card>

        {/* Section 10: Contact Us */}
        <Card className="mb-8 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-orange-50">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">10. Contact Us</h2>
            <p className="text-gray-700 mb-6">
              If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-blue-600 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Email</p>
                  <a href="mailto:info@swiftmoveandclean.com" className="text-blue-600 hover:underline">
                    info@swiftmoveandclean.com
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-blue-600 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Phone</p>
                  <a href="tel:8126694165" className="text-blue-600 hover:underline">
                    (812) 669-4165
                  </a>
                </div>
              </div>
              <div className="flex items-start space-x-3 md:col-span-2">
                <MapPin className="h-5 w-5 text-blue-600 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">Mailing Address</p>
                  <p className="text-gray-700">
                    Swift Move and Clean LLC<br />
                    130 West Brunswick Drive<br />
                    Columbus, IN 47201
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
